import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Check, Clock, ChevronDown } from 'lucide-react';
import { CardStats, Card, ExportDropdown } from '@components/ui';
import { useExportExcel } from '@hooks';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import {
    getMonthsForYear,
    availableYears,
    installmentStatuses,
    getInstallmentsMatrixByConstructionId,
    calculateMonthlyTotals
} from '@mocks/taksitMatrisi';

export default function Installments() {
    const navigate = useNavigate();
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const constructionId = showAllConstructions ? null : selectedConstruction?.id;

    const [selectedYear, setSelectedYear] = useState(2026);
    const [selectedCell, setSelectedCell] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');

    const data = getInstallmentsMatrixByConstructionId(constructionId);

    const months = useMemo(() => getMonthsForYear(selectedYear), [selectedYear]);
    const monthlyTotals = useMemo(() => calculateMonthlyTotals(data, selectedYear), [data, selectedYear]);

    // Export
    const { exportToExcel, isExporting } = useExportExcel();

    const handleExportExcel = () => {
        // Matris verisini düz tabloya çevir
        const exportData = [];
        data.forEach(person => {
            if (person.months) {
                Object.entries(person.months).forEach(([monthKey, inst]) => {
                    // Sadece seçili yıla ait ayları filtrele
                    if (monthKey.startsWith(selectedYear.toString())) {
                        const statusMap = { paid: 'Ödendi', pending: 'Bekliyor', overdue: 'Gecikmiş' };
                        exportData.push({
                            'Müşteri': person.personName,
                            'Daire': person.apartmentNo,
                            'İlgili Ay': monthKey,
                            'Tutar': inst.amount,
                            'Durum': statusMap[inst.status] || inst.status,
                        });
                    }
                });
            }
        });

        exportToExcel({
            data: exportData,
            columns: [
                { header: 'Müşteri', key: 'Müşteri' },
                { header: 'Daire', key: 'Daire' },
                { header: 'İlgili Ay', key: 'İlgili Ay' },
                { header: 'Tutar', key: 'Tutar', format: 'currency' },
                { header: 'Durum', key: 'Durum' },
            ],
            fileName: `taksitler_${selectedYear}`,
            sheetName: 'Taksitler',
        });
    };

    // Format helpers
    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatCompact = (amount) => {
        if (!amount) return '-';
        if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
        if (amount >= 1000) return `${Math.round(amount / 1000)}K`;
        return amount.toString();
    };

    // Toplam istatistikler
    // Not: Demo amaçlı o anki yılı ve belirli bir ayı baz alıyoruz. Gerçekte dayjs() gibi bir kütüphane ile current month bulunur.
    const currentMonthKey = `${selectedYear}-02`; // Örn: Şubat ayı
    const currentMonthTotals = monthlyTotals[currentMonthKey] || { paid: 0, pending: 0, overdue: 0, total: 0 };

    // Gecikmiş tüm zamanların toplamı önemlidir
    const totalOverdue = Object.values(monthlyTotals).reduce((sum, m) => sum + m.overdue, 0);

    // Hücre tıklama
    const handleCellClick = (person, monthKey, installment) => {
        if (!installment) return;
        setSelectedCell({ person, monthKey, installment });
        setPaymentAmount(installment.amount.toString()); // Varsayılan olarak taksit tutarını getir
    };

    // Modal kapat
    const closeModal = () => {
        setSelectedCell(null);
        setPaymentAmount('');
    };

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-text-primary">Taksit Takibi</h1>
                    <p className="text-text-tertiary mt-1">
                        {showAllConstructions
                            ? 'Tüm inşaatlar - Aylık taksit tablosu'
                            : `${selectedConstruction?.name || 'Seçim yapılmadı'} - Aylık taksit tablosu`}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Export */}
                    <ExportDropdown
                        onExportExcel={handleExportExcel}
                        isExporting={isExporting}
                    />
                    {/* Yıl Filtresi */}
                    <div className="relative">
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            className="appearance-none bg-bg-secondary border border-border-light rounded-lg px-4 py-2 pr-10 text-text-primary font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            {availableYears.map((year) => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" />
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <CardStats
                    title="Bu Ay Bekleyen"
                    value={formatCurrency(currentMonthTotals.pending)}
                    icon={Clock}
                    color="warning"
                    size="lg"
                />
                <CardStats
                    title="Bu Ay Tahsil Edilen"
                    value={formatCurrency(currentMonthTotals.paid)}
                    icon={Check}
                    color="success"
                    size="lg"
                />
                <CardStats
                    title="Toplam Gecikmiş"
                    value={formatCurrency(totalOverdue)}
                    icon={AlertCircle}
                    color="error"
                    size="lg"
                />
            </div>

            {/* Taksit Tablosu */}
            <Card padding="none" className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px]">
                        <thead>
                            <tr className="bg-bg-tertiary border-b border-border-light">
                                <th className="sticky left-0 bg-bg-tertiary z-10 text-left py-2 px-3 text-xs font-semibold text-text-primary min-w-[140px]">
                                    Kişi / Daire
                                </th>
                                {months.map((month) => (
                                    <th
                                        key={month.key}
                                        className="text-center py-2 px-1 text-xs font-semibold text-text-secondary min-w-[55px]"
                                    >
                                        {month.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((person, idx) => (
                                <tr
                                    key={person.id}
                                    className={clsx(
                                        "border-b border-border-light hover:bg-bg-hover transition-colors",
                                        idx % 2 === 0 ? "bg-bg-secondary" : "bg-bg-primary"
                                    )}
                                >
                                    {/* Kişi Bilgisi */}
                                    <td className="sticky left-0 z-10 py-2 px-3 bg-inherit">
                                        <div>
                                            <button
                                                onClick={() => navigate(`/finans/musteriler/${person.personId}`)}
                                                className="font-medium text-primary text-xs hover:underline cursor-pointer"
                                            >
                                                {person.personName}
                                            </button>
                                            <p className="text-2xs text-text-muted">{person.apartmentNo}</p>
                                        </div>
                                    </td>
                                    {/* Taksit Hücreleri */}
                                    {months.map((month) => {
                                        const installment = person.months[month.key];

                                        return (
                                            <td
                                                key={month.key}
                                                className="py-1.5 px-0.5 text-center"
                                            >
                                                {installment ? (
                                                    <button
                                                        onClick={() => handleCellClick(person, month.key, installment)}
                                                        className={clsx(
                                                            "inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-2xs font-medium transition-all cursor-pointer hover:scale-105 hover:shadow-sm",
                                                            installment.status === 'paid' && "bg-success-100 text-success-dark",
                                                            installment.status === 'pending' && "bg-warning-100 text-warning-dark",
                                                            installment.status === 'overdue' && "bg-error-100 text-error-dark"
                                                        )}
                                                    >
                                                        {formatCompact(installment.amount)}
                                                    </button>
                                                ) : (
                                                    <span className="text-2xs text-text-muted">-</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                            {/* Toplam Satırı */}
                            <tr className="bg-primary-50 border-t-2 border-primary font-semibold">
                                <td className="sticky left-0 z-10 py-2 px-3 bg-primary-50 text-primary font-bold text-xs">
                                    TOPLAM
                                </td>
                                {months.map((month) => {
                                    const total = monthlyTotals[month.key];
                                    return (
                                        <td key={month.key} className="py-2 px-0.5 text-center">
                                            <span className="text-2xs font-bold text-primary">
                                                {total.total > 0 ? formatCompact(total.total) : '-'}
                                            </span>
                                        </td>
                                    );
                                })}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-2xs font-medium bg-success-100 text-success-dark">50K</span>
                    <span className="text-xs text-text-secondary">Ödendi</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-2xs font-medium bg-warning-100 text-warning-dark">50K</span>
                    <span className="text-xs text-text-secondary">Bekliyor</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-2xs font-medium bg-error-100 text-error-dark">50K</span>
                    <span className="text-xs text-text-secondary">Gecikmiş</span>
                </div>
            </div>

            {/* Detay Modal */}
            {selectedCell && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeModal}>
                    <Card padding="lg" className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-text-primary">Taksit Detayı</h3>
                            <button
                                onClick={closeModal}
                                className="p-2 hover:bg-bg-hover rounded-lg transition-colors text-text-tertiary"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-text-muted">Kişi</p>
                                    <p className="font-medium text-text-primary">{selectedCell.person.personName}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-text-muted">Daire</p>
                                    <p className="font-medium text-text-primary">{selectedCell.person.apartmentNo}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-text-muted">Dönem</p>
                                    <p className="font-medium text-text-primary">
                                        {months.find(m => m.key === selectedCell.monthKey)?.fullLabel} {selectedCell.monthKey.split('-')[0]}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-text-muted">Tutar</p>
                                    <p className="font-medium text-text-primary">{formatCurrency(selectedCell.installment.amount)}</p>
                                </div>
                            </div>

                            <div>
                                <p className="text-xs text-text-muted">Durum</p>
                                <span className={clsx(
                                    "inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium mt-1",
                                    selectedCell.installment.status === 'paid' && "bg-success-100 text-success-dark",
                                    selectedCell.installment.status === 'pending' && "bg-warning-100 text-warning-dark",
                                    selectedCell.installment.status === 'overdue' && "bg-error-100 text-error-dark"
                                )}>
                                    {selectedCell.installment.status === 'paid' && <Check size={14} />}
                                    {selectedCell.installment.status === 'pending' && <Clock size={14} />}
                                    {selectedCell.installment.status === 'overdue' && <AlertCircle size={14} />}
                                    {installmentStatuses[selectedCell.installment.status].label}
                                </span>
                            </div>

                            {selectedCell.installment.paidDate && (
                                <div>
                                    <p className="text-xs text-text-muted">Ödeme Tarihi</p>
                                    <p className="font-medium text-success">{selectedCell.installment.paidDate}</p>
                                </div>
                            )}

                            {selectedCell.installment.dueDate && selectedCell.installment.status !== 'paid' && (
                                <div>
                                    <p className="text-xs text-text-muted">Vade Tarihi</p>
                                    <p className={clsx(
                                        "font-medium",
                                        selectedCell.installment.status === 'overdue' ? "text-error" : "text-warning"
                                    )}>
                                        {selectedCell.installment.dueDate}
                                    </p>
                                </div>
                            )}

                            {/* Planlanan Ödeme Aracı ERP Detayı */}
                            <div className="pt-2 border-t border-border-light flex flex-col gap-1.5">
                                <p className="text-xs text-text-muted mb-1">Planlanan Ödeme Aracı</p>
                                <div className="flex items-center gap-2">
                                    <span className="inline-flex px-2 py-1 bg-bg-secondary border border-border-light rounded-md text-xs font-medium text-text-secondary">
                                        {selectedCell.installment.documentType === 'cek' ? '📄 Vadeli Çek' :
                                            selectedCell.installment.documentType === 'senet' ? '📄 Senet' :
                                                '💵 Banka Transferi / Nakit'}
                                    </span>
                                    {selectedCell.installment.status === 'pending' && selectedCell.installment.documentType && (
                                        <span className="text-[10px] text-primary/80 italic flex-1">
                                            *Sistemde bu taksite bağlı {selectedCell.installment.documentType === 'cek' ? "çek" : "senet"} bulunuyor.
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {selectedCell.installment.status !== 'paid' && (
                            <div className="mt-5 p-4 bg-bg-secondary rounded-xl border border-border-light space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-text-secondary mb-2">
                                        Tahsilat Yöntemi
                                    </label>
                                    <select className="w-full px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary">
                                        <option value="nakit">Havale/EFT (Evrak İade Edilecek)</option>
                                        <option value="cek">Yeni Çek Alındı</option>
                                        <option value="senet">Mevcut Evrak Kapatıldı</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-text-secondary mb-2">
                                        Tahsil Edilen Tutar (₺)
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="number"
                                            value={paymentAmount}
                                            onChange={(e) => setPaymentAmount(e.target.value)}
                                            className="w-full px-3 py-2 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary font-bold"
                                            placeholder="Tutar girin..."
                                        />
                                    </div>
                                    <p className="text-2xs text-text-muted mt-2">
                                        Parçalı tahsilat durumunda kalan tutar taksit planında görünmeye devam eder.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="mt-6 flex gap-2">
                            {selectedCell.installment.status !== 'paid' && (
                                <button
                                    onClick={() => {
                                        toast.success(`${selectedCell.person.personName} kişisinden ${paymentAmount}₺ tahsilat başarıyla alındı.`);
                                        closeModal();
                                    }}
                                    className="flex-1 py-2 px-4 bg-success text-white rounded-lg hover:bg-success-dark transition-colors font-medium text-sm"
                                >
                                    Tahsilatı Kaydet
                                </button>
                            )}
                            <button
                                onClick={closeModal}
                                className="flex-1 py-2 px-4 bg-bg-tertiary text-text-primary rounded-lg hover:bg-bg-hover transition-colors font-medium text-sm"
                            >
                                Kapat
                            </button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}
